import { GridSortModel } from '@mui/x-data-grid';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SeamStatus } from 'app/constants/seam-status';
import { SeamRequest } from 'app/model/seam-request';
import { User } from 'app/model/user';
import { AppThunk, RootState } from 'app/store';

import { fetchSeamRequests } from '../seam/slice';

interface ExploreState {
    status: SeamStatus | '';
    term: string;
    assignedTo?: User;
    tuner?: User;
    results: SeamRequest[];
    sortModel: GridSortModel;

    createdAfter?: string;
    createdBefore?: string;
    preprodDeployAfter?: string;
    preprodDeployBefore?: string;
    prodDeployAfter?: string;
    prodDeployBefore?: string;
}

const initialState: ExploreState = {
    status: '',
    term: '',
    results: [],
    sortModel: [],
};

export const ExploreSlice = createSlice({
    name: 'explore',
    initialState,
    reducers: {
        setStatus: (state, action: PayloadAction<SeamStatus | ''>) => {
            state.status = action.payload;
        },
        setTerm: (state, action: PayloadAction<string>) => {
            state.term = action.payload;
        },
        setAssignedTo: (state, action: PayloadAction<User | undefined>) => {
            state.assignedTo = action.payload;
        },
        setTuner: (state, action: PayloadAction<User | undefined>) => {
            state.tuner = action.payload;
        },
        setResults: (state, action: PayloadAction<SeamRequest[]>) => {
            state.results = action.payload;
        },
        setSortModel: (state, action: PayloadAction<GridSortModel>) => {
            state.sortModel = action.payload;
        },
        setCreatedAfter: (state, action: PayloadAction<string | undefined>) => {
            state.createdAfter = action.payload;
        },
        setCreatedBefore: (state, action: PayloadAction<string | undefined>) => {
            state.createdBefore = action.payload;
        },
        setPreProdDeployAfter: (state, action: PayloadAction<string | undefined>) => {
            state.preprodDeployAfter = action.payload;
        },
        setPreProdDeployBefore: (state, action: PayloadAction<string | undefined>) => {
            state.preprodDeployBefore = action.payload;
        },
        setProdDeployAfter: (state, action: PayloadAction<string | undefined>) => {
            state.prodDeployAfter = action.payload;
        },
        setProdDeployBefore: (state, action: PayloadAction<string | undefined>) => {
            state.prodDeployBefore = action.payload;
        },
    },
});

// main reducer.
export const ExploreReducer = ExploreSlice.reducer;

// selectors
export const selectStatus = (state: RootState): SeamStatus | '' => state.explore.status;
export const selectTerm = (state: RootState): string => state.explore.term;
export const selectAssignedTo = (state: RootState): User | undefined => state.explore.assignedTo;
export const selectTuner = (state: RootState): User | undefined => state.explore.tuner;
export const selectResults = (state: RootState): SeamRequest[] => state.explore.results;
export const selectSortModel = (state: RootState): GridSortModel => state.explore.sortModel;
export const selectCreatedAfter = (state: RootState): string | undefined => state.explore.createdAfter;
export const selectCreatedBefore = (state: RootState): string | undefined => state.explore.createdBefore;
export const selectPreprodDeployAfter = (state: RootState): string | undefined => state.explore.preprodDeployAfter;
export const selectPreprodDeployBefore = (state: RootState): string | undefined => state.explore.preprodDeployBefore;
export const selectProdDeployAfter = (state: RootState): string | undefined => state.explore.prodDeployAfter;
export const selectProdDeployBefore = (state: RootState): string | undefined => state.explore.prodDeployBefore;

// actions
export const {
    setStatus,
    setTerm,
    setAssignedTo,
    setTuner,
    setResults,
    setSortModel,
    setCreatedAfter,
    setCreatedBefore,
    setPreProdDeployAfter,
    setPreProdDeployBefore,
    setProdDeployAfter,
    setProdDeployBefore,
} = ExploreSlice.actions;

// async actions
export const findRequests = (): AppThunk => (dispatch, getState) => {
    const state = getState();
    const status = selectStatus(state);
    dispatch(
        fetchSeamRequests({
            status: '' === status ? undefined : status,
            assignee: selectAssignedTo(state),
            tuner: selectTuner(state),
            term: selectTerm(state),
            createdAfter: selectCreatedAfter(state),
            createdBefore: selectCreatedBefore(state),
            preprodDeployAfter: selectPreprodDeployAfter(state),
            preprodDeployBefore: selectPreprodDeployBefore(state),
            prodDeployAfter: selectProdDeployAfter(state),
            prodDeployBefore: selectProdDeployBefore(state),
        }),
    ).subscribe((value) => dispatch(setResults(value)));
};

export const downloadRequests = (): AppThunk => (dispatch, getState) => {
    const state = getState();
    const status = selectStatus(state);
    dispatch(
        fetchSeamRequests({
            status: '' === status ? undefined : status,
            assignee: selectAssignedTo(state),
            tuner: selectTuner(state),
            term: selectTerm(state),
            createdAfter: selectCreatedAfter(state),
            createdBefore: selectCreatedBefore(state),
            preprodDeployAfter: selectPreprodDeployAfter(state),
            preprodDeployBefore: selectPreprodDeployBefore(state),
            prodDeployAfter: selectProdDeployAfter(state),
            prodDeployBefore: selectProdDeployBefore(state),
            download: true,
        }),
    ).subscribe(); // interceptor should handle the download
};
